import { useContext, useEffect, useState } from "react";
import { useConstants } from "../../hooks/UseConstants";
import AuthContext from "../../context/AuthContext";
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { useTableStyles } from "../../hooks/UseTableStyles";
import { useNavigate } from "react-router-dom";
import { useWaits } from "../../hooks/UseWait";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import { FormattedMessage } from "react-intl";
import AdminPageHero from "../../components/AdminPageHero";
import AdminFinanceNav from "../../components/AdminFinanceNav";
import PageContent from "../../components/PageContent";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import PercentChangeFooter from "../../helper/PercentChangeFooter";
import { formatCurrency } from "../../helper/formatCurrency";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";

const emptyCards = {
    total_sales: { total: 0, percent: 0 },
    total_admin_revenue: { total: 0, percent: 0 },
    total_teacher_revenue: { total: 0, percent: 0 },
    total_student_wallets: { total: 0, percent: 0 },
};

function Financial() {
    const navigate = useNavigate();
    const { wait } = useContext(AuthContext);
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [systemProfits, setSystemProfits] = useState('');
    const [teacherProfits, setTeacherProfits] = useState('');
    const [sales, setSales] = useState([]);
    const [cards, setCards] = useState(emptyCards);
    const [pendingWithdrawCount, setPendingWithdrawCount] = useState(0);

    const visibleSales = sales.slice(0, 5);

    const calculatingPercentage = (total, profit) => {
        if (!total) return 0;
        return Math.round((100 * profit) / total);
    };

    const loadFinancialData = async () => {
        const [salesRes, configRes, pendingRes] = await Promise.all([
            Fetch(host + '/reports/sales?is_detailed=1', 'GET', null),
            Fetch(host + '/lists/config?keys[]=admin_percentage', 'GET', null),
            Fetch(host + '/admin/profit/withdrawal-requests?status=pending&per_page=1', 'GET', null),
        ]);

        if (salesRes.status === 200) {
            const payload = salesRes.data?.data;
            if (Array.isArray(payload)) {
                setSales(Array.isArray(payload[0]?.data) ? payload[0].data : []);
                setCards({ ...emptyCards, ...(payload[1] || {}) });
            }
        }

        if (configRes.status === 200) {
            const pct = configRes.data?.data?.admin_percentage;
            if (pct != null && pct !== '') {
                setSystemProfits(String(pct));
                setTeacherProfits(String(100 - Number(pct)));
            }
        }

        if (pendingRes.status === 200) {
            const { pagination } = parsePaginatedResponse(pendingRes);
            setPendingWithdrawCount(pagination.total ?? 0);
        } else {
            setPendingWithdrawCount(0);
        }

        setGetWait(false);
    };

    const updatePercentage = async () => {
        setSendWait(true);
        const formData = new FormData();
        formData.append('admin_percentage', systemProfits);
        const result = await Fetch(host + '/admin/config/update', 'POST', formData);

        if (result.status === 200) {
            setSnackBar('success', isAr ? 'تم تعديل عمولة المنصة بنجاح' : 'Platform commission updated successfully');
        } else if (result.status === 422) {
            setSnackBar('error', result.data?.errors?.[0] || (isAr ? 'خطأ في البيانات' : 'Validation error'));
        }

        setSendWait(false);
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            loadFinancialData();
        }
    }, [wait]);

    const viewAllLink = (path) => (
        <Typography
            onClick={() => navigate(path)}
            variant="body2"
            className="cursor-pointer"
            sx={{ color: '#2563EB', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
            <FormattedMessage id="view_record" />
            {isAr ? <ArrowBackIosNewIcon fontSize="small" /> : <ArrowForwardIosOutlinedIcon fontSize="small" />}
        </Typography>
    );

    const teacherInitials = (name) => {
        if (!name) return '?';
        const parts = String(name).trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return '?';
        return `${parts[0]?.charAt(0) || ''}${parts[1]?.charAt(0) || ''}`.toUpperCase();
    };

    const saleTypeLabel = (type) => {
        if (type === 'Course') return isAr ? 'شراء دورة' : 'Course purchase';
        if (type === 'Path') return isAr ? 'شراء مسار' : 'Path purchase';
        return type || '—';
    };

    return (
        <>
            <PageContent wait={wait} loading={getWait && !wait}>
                <AdminPageHero />
                <AdminFinanceNav />

                {pendingWithdrawCount > 0 && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2, borderRadius: 2, cursor: 'pointer' }}
                        onClick={() => navigate('/financial/withdrawal-requests')}
                        action={
                            <Button color="inherit" size="small" onClick={() => navigate('/financial/withdrawal-requests')}>
                                {isAr ? 'مراجعة الطلبات' : 'Review requests'}
                            </Button>
                        }
                    >
                        {isAr
                            ? `لديك ${pendingWithdrawCount} طلب سحب معلّق بانتظار المراجعة.`
                            : `You have ${pendingWithdrawCount} pending withdrawal request(s) awaiting review.`}
                    </Alert>
                )}

                <ScrollReveal>
                    <Box className="flex flex-wrap gap-2 mb-3">
                        <DashboardStatCard
                            label={isAr ? 'طلبات سحب معلّقة' : 'Pending withdrawals'}
                            value={String(pendingWithdrawCount)}
                            icon={<PaymentsOutlinedIcon />}
                            accent="indigo"
                            footer={isAr ? 'اضغط للمراجعة والموافقة' : 'Tap to review and approve'}
                            onClick={() => navigate('/financial/withdrawal-requests')}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                        <DashboardStatCard
                            label={<FormattedMessage id="total_sales" />}
                            value={formatCurrency(cards.total_sales?.total ?? 0, isAr)}
                            icon={<AttachMoneyOutlinedIcon />}
                            accent="green"
                            footer={<PercentChangeFooter percent={cards.total_sales?.percent ?? 0} />}
                            onClick={() => navigate('/financial/financial-operations')}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                        <DashboardStatCard
                            label={<FormattedMessage id="total_platform_commission" />}
                            value={formatCurrency(cards.total_admin_revenue?.total ?? 0, isAr)}
                            icon={<AccountBalanceOutlinedIcon />}
                            accent="blue"
                            footer={<PercentChangeFooter percent={cards.total_admin_revenue?.percent ?? 0} />}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                        <DashboardStatCard
                            label={<FormattedMessage id="total_teacher_earnings" />}
                            value={formatCurrency(cards.total_teacher_revenue?.total ?? 0, isAr)}
                            icon={<SchoolOutlinedIcon />}
                            accent="teal"
                            footer={<PercentChangeFooter percent={cards.total_teacher_revenue?.percent ?? 0} />}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                        <DashboardStatCard
                            label={<FormattedMessage id="student_wallets" />}
                            value={formatCurrency(cards.total_student_wallets?.total ?? 0, isAr)}
                            icon={<AccountBalanceWalletOutlinedIcon />}
                            accent="sky"
                            footer={<PercentChangeFooter percent={cards.total_student_wallets?.percent ?? 0} />}
                            onClick={() => navigate('/wallet')}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                    </Box>
                </ScrollReveal>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, lg: 4 }}>
                        <ScrollReveal delay={60}>
                            <DashboardPanelCard
                                title={<FormattedMessage id="revenue_distribution_settings" />}
                                subtitle={isAr ? 'نسبة عمولة المنصة من كل عملية بيع' : 'Platform share on each sale'}
                                className="mx-0 h-full"
                            >
                                <TextField
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label={<FormattedMessage id="platform_commission_percentage" />}
                                    value={systemProfits}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setSystemProfits(v);
                                        const n = Number(v);
                                        setTeacherProfits(v === '' ? '' : String(100 - n));
                                    }}
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label={<FormattedMessage id="teachers_earnings_percentage" />}
                                    value={teacherProfits}
                                    InputProps={{ readOnly: true }}
                                    sx={{ mb: 2 }}
                                />
                                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                    <FormattedMessage id="revenue_distribution_settings_note" />
                                </Alert>
                                <Button
                                    onClick={updatePercentage}
                                    variant="contained"
                                    fullWidth
                                    disabled={sendWait || systemProfits === ''}
                                    startIcon={sendWait ? null : <SaveOutlinedIcon />}
                                    sx={{
                                        py: 1.25,
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #0D358C 0%, #2563EB 100%)',
                                    }}
                                >
                                    {sendWait ? (
                                        <CircularProgress size={22} color="inherit" />
                                    ) : (
                                        <FormattedMessage id="save_changes" />
                                    )}
                                </Button>
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 8 }}>
                        <ScrollReveal delay={100}>
                            <DashboardPanelCard
                                title={<FormattedMessage id="earnings_record" />}
                                subtitle={isAr ? 'آخر أرباح المدرسين الناتجة من عمليات البيع' : 'Latest teacher earnings generated from sales'}
                                className="mx-0 h-full"
                                headerAction={viewAllLink('/financial/financial-operations')}
                            >
                                {visibleSales.length > 0 ? (
                                    <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                        <FormattedMessage id="teacher_name" />
                                                    </StyledTableCell>
                                                    <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                        <FormattedMessage id="teacher_earnings" />
                                                    </StyledTableCell>
                                                    <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                        <FormattedMessage id="date" />
                                                    </StyledTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {visibleSales.map((sale) => (
                                                    <StyledTableRow key={sale.id}>
                                                        <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: isAr ? 'row' : 'row-reverse', justifyContent: isAr ? 'flex-start' : 'flex-end' }}>
                                                                <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: '#0D358C' }}>
                                                                    {teacherInitials(sale.teacher)}
                                                                </Avatar>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {sale.teacher || '-'}
                                                                </Typography>
                                                            </Box>
                                                        </StyledTableCell>
                                                        <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                            {formatCurrency(sale.teacher_profit, isAr)}
                                                        </StyledTableCell>
                                                        <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                            {sale.date || '-'}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                                        <FormattedMessage id="nothing" />
                                    </Typography>
                                )}
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Grid>
                </Grid>

                <ScrollReveal delay={140}>
                    <DashboardPanelCard
                        title={<FormattedMessage id="record_financial_transactions" />}
                        subtitle={isAr ? 'آخر عمليات الشراء والعمولات' : 'Latest purchases and commissions'}
                        className="mx-0"
                        headerAction={viewAllLink('/financial/financial-operations')}
                    >
                        {visibleSales.length > 0 ? (
                            <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_id" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_type" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_details" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="student_name" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="teacher_name" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="amount" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="platform_commission" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="teacher_earnings" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_date" /></StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleSales.map((sale) => (
                                            <StyledTableRow key={sale.id}>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{sale.id}</StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{saleTypeLabel(sale.type)}</StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                    {isAr ? (sale.name_ar || sale.name_en) : (sale.name_en || sale.name_ar)}
                                                </StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{sale.student}</StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{sale.teacher}</StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{formatCurrency(sale.amount, isAr)}</StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'} sx={{ fontWeight: 700, color: '#2563EB' }}>
                                                    {calculatingPercentage(sale.amount, sale.admin_profit)}%
                                                </StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'} sx={{ fontWeight: 700 }}>
                                                    {formatCurrency(sale.teacher_profit, isAr)}
                                                </StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{sale.date}</StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                                <FormattedMessage id="nothing" />
                            </Typography>
                        )}
                    </DashboardPanelCard>
                </ScrollReveal>
            </PageContent>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Financial;
